import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { ShieldAlert } from "lucide-react";
import { ApiError, postJSON } from "../../lib/api";
import { AuthLayout } from "../../components/layout/AuthLayout";
import { PasswordInput } from "../../components/ui/PasswordInput";
import { Button } from "../../components/ui/Button";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await postJSON<void>("/auth/reset-password", { token, password });
      navigate("/login", { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Reset link is invalid or expired",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout>
      <h1>Choose a new password</h1>
      <p className="auth-card__subtitle">
        Create a strong password for your Zirro account.
      </p>

      {token ? (
        <form className="auth-form" onSubmit={onSubmit}>
          <PasswordInput
            label="New password"
            name="password"
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={72}
            placeholder="Enter your new password"
            hint="At least 8 characters. Saving signs you out of every device."
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />

          {error ? (
            <div className="form-error" role="alert">
              {error}
            </div>
          ) : null}

          <Button type="submit" loading={pending} fullWidth size="lg">
            Set new password
          </Button>
        </form>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "var(--z-space-4)",
            padding: "var(--z-space-8) var(--z-space-4)",
            background: "var(--z-error-50)",
            borderRadius: "var(--z-radius-lg)",
            textAlign: "center",
          }}
          role="alert"
        >
          <ShieldAlert size={40} style={{ color: "var(--z-error-500)" }} />
          <p style={{ color: "var(--z-gray-700)", fontSize: "0.9375rem" }}>
            This reset link is missing its token. Please request a new one.
          </p>
        </div>
      )}

      <div className="auth-card__footer">
        <p>
          <Link to="/forgot-password">Request a new link</Link>
        </p>
      </div>
    </AuthLayout>
  );
}
